import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import './PainelGestor.css';
import './DataPage.css';
import { formatDate, formatDateTime } from '../utils/formatDate';

function getDateStr(date) {
  const d = date || new Date();
  return d.toISOString().split('T')[0];
}

function get30DaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return getDateStr(d);
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];

const STATUS_LABELS = {
  enviado: 'Enviado', entregue: 'Entregue', lido: 'Lido',
  erro: 'Erro', pendente: 'Pendente'
};

const STATUS_COLORS = {
  enviado: '#3b82f6', entregue: '#10b981', lido: '#8b5cf6',
  erro: '#ef4444', pendente: '#f59e0b'
};

const TIPO_LABELS = {
  manual: 'Manual', massa: 'Em Massa', automatico: 'Automatico'
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

function WhatsAppDashboard() {
  const isMobile = useIsMobile();
  const [dataInicio, setDataInicio] = useState(get30DaysAgoStr());
  const [dataFim, setDataFim] = useState(getDateStr());
  const [filterOpen, setFilterOpen] = useState(false);

  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [recentErrors, setRecentErrors] = useState([]);
  const [webhookActivity, setWebhookActivity] = useState([]);
  const [activeWindows, setActiveWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('graficos');

  // Novos dados operacionais
  const [kpis, setKpis] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [funil, setFunil] = useState(null);
  const [rankings, setRankings] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);

      const [statsRes, chartRes, errorsRes, webhookRes, windowsRes, kpisRes, tsRes, funilRes, ranksRes] = await Promise.all([
        axios.get(`${API_URL}/whatsapp/dashboard/stats?${params.toString()}`),
        axios.get(`${API_URL}/whatsapp/dashboard/chart-data?${params.toString()}`),
        axios.get(`${API_URL}/whatsapp/dashboard/recent-errors`),
        axios.get(`${API_URL}/whatsapp/dashboard/webhook-activity?limit=30`),
        axios.get(`${API_URL}/whatsapp/dashboard/active-windows`),
        axios.get(`${API_URL}/whatsapp/dashboard/kpis`),
        axios.get(`${API_URL}/whatsapp/dashboard/timeseries?days=30`),
        axios.get(`${API_URL}/whatsapp/dashboard/funil`),
        axios.get(`${API_URL}/whatsapp/dashboard/rankings?days=7`),
      ]);

      setStats(statsRes.data);
      setChartData(chartRes.data);
      setRecentErrors(errorsRes.data);
      setWebhookActivity(webhookRes.data);
      setActiveWindows(windowsRes.data);
      setKpis(kpisRes.data);
      setTimeseries(tsRes.data || []);
      setFunil(funilRes.data);
      setRankings(ranksRes.data);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados do dashboard.');
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // formatDate e formatDateTime importados de ../utils/formatDate

  const formatPhone = (phone) => {
    if (!phone) return '-';
    if (phone.length === 13 && phone.startsWith('55')) {
      return `+${phone.substring(0,2)} (${phone.substring(2,4)}) ${phone.substring(4,9)}-${phone.substring(9)}`;
    }
    return phone;
  };

  return (
    <div className="gestao-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>📊</span>
          <div>
            <h1 style={styles.pageTitle}>Dashboard WhatsApp</h1>
            <p style={styles.pageSubtitle}>Visão gerencial dos envios e interações via WhatsApp</p>
          </div>
        </div>
      </div>

      <div className={`gestao-filter-bar ${filterOpen ? 'filter-open' : ''}`}>
        <button className="filter-toggle-btn" onClick={() => setFilterOpen(!filterOpen)}>
          {filterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          <span className={`filter-toggle-arrow ${filterOpen ? 'open' : ''}`}>&#9660;</span>
        </button>
        <div className={`filter-row ${filterOpen ? 'filter-visible' : ''}`}>
          <div className="filter-group">
            <label>Periodo</label>
            <div className="date-range">
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
              <span className="date-separator">ate</span>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn-apply" onClick={() => { fetchData(); setFilterOpen(false); }}>Aplicar Filtros</button>
            <button className="btn-reset" onClick={() => { setDataInicio(get30DaysAgoStr()); setDataFim(getDateStr()); }}>Limpar</button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="gestao-loading">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      )}

      {error && <div className="gestao-error">{error}</div>}

      {stats && !loading && (
        <>
          {/* NOVO: Bloco operacional (KPIs + Funil + Timeseries) */}
          {kpis && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
              {Object.entries(kpis.hoje_por_tipo || {}).map(([tipo, info]) => {
                const ontem = kpis.ontem_por_tipo?.[tipo] || 0;
                const delta = ontem ? Math.round(((info.total - ontem) / ontem) * 100) : (info.total > 0 ? 100 : 0);
                return (
                  <div key={tipo} style={{ background: '#fff', padding: 14, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                    <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{tipo.replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e' }}>{info.total}</div>
                    <div style={{ fontSize: 11, color: delta >= 0 ? '#2e7d32' : '#c62828', marginTop: 2 }}>
                      {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}% vs ontem
                    </div>
                  </div>
                );
              })}
              {funil && (
                <div style={{ background: '#fff', padding: 14, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.08)', gridColumn: 'span 2' }}>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Funil últimas 24h</div>
                  {[
                    { label: 'Enviadas', val: funil.enviadas_24h, color: '#3b82f6' },
                    { label: 'Entregues', val: funil.entregues_24h, color: '#10b981' },
                    { label: 'Lidas', val: funil.lidas_24h, color: '#8b5cf6' },
                  ].map(r => {
                    const pct = funil.enviadas_24h ? Math.round((r.val / funil.enviadas_24h) * 100) : 0;
                    return (
                      <div key={r.label} style={{ marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                          <span>{r.label}</span><span><strong>{r.val}</strong> · {pct}%</span>
                        </div>
                        <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: r.color, transition: 'width .3s' }} />
                        </div>
                      </div>
                    );
                  })}
                  {funil.erros_24h > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#c62828' }}>⚠️ {funil.erros_24h} erros nas últimas 24h</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Timeseries últimos 30 dias */}
          {timeseries.length > 0 && (
            <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>📈 Envios diários (últimos 30 dias)</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={timeseries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="dia" tickFormatter={d => d.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="enviado" stackId="1" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.5} />
                  <Area type="monotone" dataKey="entregue" stackId="1" fill="#10b981" stroke="#10b981" fillOpacity={0.5} />
                  <Area type="monotone" dataKey="lido" stackId="1" fill="#8b5cf6" stroke="#8b5cf6" fillOpacity={0.5} />
                  <Area type="monotone" dataKey="erro" stackId="1" fill="#ef4444" stroke="#ef4444" fillOpacity={0.7} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Rankings: top templates + erros */}
          {rankings && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>🏆 Top templates (7 dias)</div>
                <table style={{ width: '100%', fontSize: 13 }}>
                  <thead><tr><th style={{ textAlign: 'left', padding: 6, color: '#888' }}>Template</th><th style={{ textAlign: 'right', padding: 6, color: '#888' }}>Total</th><th style={{ textAlign: 'right', padding: 6, color: '#888' }}>Erros</th></tr></thead>
                  <tbody>
                    {(rankings.templates || []).map(r => (
                      <tr key={r.template_nome} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: 6, fontSize: 12 }}>{r.template_nome}</td>
                        <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{r.total}</td>
                        <td style={{ padding: 6, textAlign: 'right', color: r.erros > 0 ? '#c62828' : '#999' }}>{r.erros || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 10 }}>⚠️ Top erros (7 dias)</div>
                {(rankings.erros || []).length === 0 ? (
                  <div style={{ padding: 16, color: '#2e7d32', fontSize: 13, textAlign: 'center' }}>✅ Nenhum erro registrado</div>
                ) : (
                  <table style={{ width: '100%', fontSize: 12 }}>
                    <thead><tr><th style={{ textAlign: 'left', padding: 6, color: '#888' }}>Erro</th><th style={{ textAlign: 'right', padding: 6, color: '#888' }}>Total</th></tr></thead>
                    <tbody>
                      {rankings.erros.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                          <td style={{ padding: 6, color: '#c62828' }}>{(r.erro || '').substring(0, 80)}</td>
                          <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{r.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Stat Cards */}
          <div className="compact-stats">
            <div className="compact-stat-card blue">
              <div className="compact-stat-value">{(stats.total || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Total Enviados</div>
            </div>
            <div className="compact-stat-card green">
              <div className="compact-stat-value">{(stats.entregues || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Entregues</div>
            </div>
            <div className="compact-stat-card purple">
              <div className="compact-stat-value">{(stats.lidos || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Lidos</div>
            </div>
            <div className="compact-stat-card orange" style={{ '--card-color': '#ef4444', borderLeftColor: '#ef4444' }}>
              <div className="compact-stat-value">{(stats.erros || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Erros</div>
            </div>
            <div className="compact-stat-card teal">
              <div className="compact-stat-value">{(stats.respostas_botao || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Respostas Botao</div>
            </div>
            <div className="compact-stat-card cyan">
              <div className="compact-stat-value">{(stats.mensagens_recebidas || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Msg Recebidas</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {['graficos', 'erros', 'webhook', 'janelas'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: activeTab === tab ? '2px solid #3b82f6' : '1px solid #d1d5db',
                  background: activeTab === tab ? '#eff6ff' : '#fff',
                  color: activeTab === tab ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                {tab === 'graficos' ? 'Graficos' : tab === 'erros' ? 'Erros Recentes' : tab === 'webhook' ? 'Atividade Webhook' : 'Janelas Ativas'}
              </button>
            ))}
          </div>

          {activeTab === 'graficos' && chartData && (
            <div className="gestao-charts-grid">
              {/* AreaChart: Envios por dia */}
              <div className="gestao-chart-card" style={{ gridColumn: 'span 2' }}>
                <h3>Envios por Dia</h3>
                {chartData.por_dia?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <AreaChart data={chartData.por_dia.map(d => ({ ...d, data: formatDate(d.data) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="enviados" name="Enviados" stroke="#10b981" fill="#10b98133" />
                      <Area type="monotone" dataKey="erros" name="Erros" stroke="#ef4444" fill="#ef444433" />
                      <Area type="monotone" dataKey="total" name="Total" stroke="#3b82f6" fill="#3b82f633" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Nenhum dado no periodo</p>
                )}
              </div>

              {/* PieChart: Por Status */}
              <div className="gestao-chart-card">
                <h3>Distribuicao por Status</h3>
                {chartData.por_status?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <PieChart>
                      <Pie
                        data={chartData.por_status.map(s => ({ ...s, name: STATUS_LABELS[s.status] || s.status }))}
                        dataKey="total"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={isMobile ? 60 : 100}
                        label={isMobile ? false : ({ name, total }) => `${name} (${total})`}
                      >
                        {chartData.por_status.map((entry, index) => (
                          <Cell key={index} fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Sem dados</p>
                )}
              </div>

              {/* BarChart: Por Tipo */}
              <div className="gestao-chart-card">
                <h3>Envios por Tipo</h3>
                {chartData.por_tipo?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart data={chartData.por_tipo.map(t => ({ ...t, nome: TIPO_LABELS[t.tipo] || t.tipo }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Sem dados</p>
                )}
              </div>

              {/* BarChart: Por Local */}
              <div className="gestao-chart-card" style={{ gridColumn: 'span 2' }}>
                <h3>Envios por Local/Unidade</h3>
                {chartData.por_local?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart data={chartData.por_local}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="local" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="total" name="Total" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                        {chartData.por_local.map((entry, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Sem dados</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'erros' && (
            <div className="gestao-chart-card">
              <h3>Ultimos Erros de Envio</h3>
              {recentErrors.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Paciente</th>
                        <th>Telefone</th>
                        <th>Tipo</th>
                        <th>Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentErrors.map(err => (
                        <tr key={err.id}>
                          <td>{formatDateTime(err.enviado_em)}</td>
                          <td>{err.paciente_nome || '-'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{formatPhone(err.telefone)}</td>
                          <td>{err.tipo || '-'}</td>
                          <td style={{ color: '#ef4444', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={err.erro}>{err.erro || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Nenhum erro recente</p>
              )}
            </div>
          )}

          {activeTab === 'webhook' && (
            <div className="gestao-chart-card">
              <h3>Atividade de Webhook Recente</h3>
              {webhookActivity.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Telefone</th>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>Conteudo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webhookActivity.map(msg => (
                        <tr key={msg.id}>
                          <td>{formatDateTime(msg.timestamp_msg)}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{formatPhone(msg.from_phone)}</td>
                          <td>{msg.from_name || '-'}</td>
                          <td>
                            <span style={{
                              padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                              background: msg.type === 'text' ? '#dbeafe' : msg.type === 'button' ? '#fef3c7' : '#e5e7eb',
                              color: msg.type === 'text' ? '#1d4ed8' : msg.type === 'button' ? '#92400e' : '#374151'
                            }}>
                              {msg.type}
                            </span>
                          </td>
                          <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={msg.body}>{msg.body || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Nenhuma atividade recente</p>
              )}
            </div>
          )}

          {activeTab === 'janelas' && (
            <div className="gestao-chart-card">
              <h3>Janelas de Conversa Ativas (24h)</h3>
              {activeWindows.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th>Telefone</th>
                        <th>Nome</th>
                        <th>Ultima Mensagem</th>
                        <th>Tipo</th>
                        <th>Expira em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeWindows.map((w, i) => {
                        const expiresIn = w.conversation_window_expires ? Math.max(0, Math.round((new Date(w.conversation_window_expires) - new Date()) / 60000)) : 0;
                        return (
                          <tr key={i}>
                            <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{formatPhone(w.from_phone)}</td>
                            <td>{w.from_name || '-'}</td>
                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={w.body}>{w.body || '-'}</td>
                            <td>{w.type || '-'}</td>
                            <td>
                              <span style={{
                                padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                                background: expiresIn > 360 ? '#dcfce7' : expiresIn > 60 ? '#fef3c7' : '#fecaca',
                                color: expiresIn > 360 ? '#166534' : expiresIn > 60 ? '#92400e' : '#991b1b'
                              }}>
                                {expiresIn > 60 ? `${Math.round(expiresIn / 60)}h ${expiresIn % 60}m` : `${expiresIn}m`}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '40px' }}>Nenhuma janela de conversa ativa</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WhatsAppDashboard;
